import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

const STORAGE_KEY = "kinly.tokens";

export function getTokens() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

export function setTokens(tokens) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

export function clearTokens() {
  localStorage.removeItem(STORAGE_KEY);
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// ─────────── Persistent + in-memory GET response cache ────────────────
//
// WhatsApp-style instant cold-start: every GET response is also written
// to IndexedDB so the NEXT cold start hydrates this in-memory cache from
// disk BEFORE any React component renders. Result: tabs paint in <50 ms
// with last-known data, while a background revalidate fires silently.
//
//   _getCache    in-memory mirror (read by pages via getCached())
//   IndexedDB    persistent (survives app close + reinstall on same device)
//
// Hydration runs once in App.js boot before <BrowserRouter> mounts.
import { get as idbGet, set as idbSet, del as idbDel, keys as idbKeys } from "idb-keyval";

const _getCache = new Map(); // url -> { data, ts }
const CACHE_TTL_MS = 60_000;
const IDB_PREFIX = "kly.api.";
const MAX_PERSISTED = 200; // soft cap to avoid runaway IndexedDB growth

export function getCached(url) {
  const entry = _getCache.get(url);
  if (!entry) return null;
  return entry.data;
}

export function setCached(url, data) {
  _getCache.set(url, { data, ts: Date.now() });
  // Best-effort persist; don't await so axios responses are never blocked.
  try { idbSet(IDB_PREFIX + url, { data, ts: Date.now() }); } catch { /* noop */ }
}

export function clearCachePrefix(prefix) {
  for (const key of _getCache.keys()) {
    if (key.startsWith(prefix)) _getCache.delete(key);
  }
  // Persistent side too — fire-and-forget.
  (async () => {
    try {
      const all = await idbKeys();
      for (const k of all) {
        if (typeof k === "string" && k.startsWith(IDB_PREFIX + prefix)) {
          idbDel(k);
        }
      }
    } catch { /* noop */ }
  })();
}

/**
 * Called once at app boot (before any component renders) to load the
 * previous session's responses into _getCache. After this completes,
 * every screen's `useState(() => getCached(url))` initialiser returns
 * cached data immediately — no blank-screen flash, no spinner.
 *
 * Caps at MAX_PERSISTED entries (evicts oldest by ts) so IndexedDB
 * doesn't grow unbounded across months of use.
 */
export async function hydrateCacheFromDisk() {
  try {
    const all = await idbKeys();
    const apiKeys = all.filter((k) => typeof k === "string" && k.startsWith(IDB_PREFIX));
    const entries = await Promise.all(
      apiKeys.map(async (k) => [k.slice(IDB_PREFIX.length), await idbGet(k)]),
    );
    // Soft-cap eviction
    if (entries.length > MAX_PERSISTED) {
      entries.sort((a, b) => (a[1]?.ts || 0) - (b[1]?.ts || 0));
      const toEvict = entries.slice(0, entries.length - MAX_PERSISTED);
      for (const [url] of toEvict) idbDel(IDB_PREFIX + url);
      entries.splice(0, entries.length - MAX_PERSISTED);
    }
    for (const [url, entry] of entries) {
      if (entry && entry.data !== undefined) _getCache.set(url, entry);
    }
    if (entries.length > 0) {
      console.log(`[Kinly] hydrated ${entries.length} cached responses from disk`);
    }
  } catch (e) {
    console.warn("[Kinly] cache hydration skipped:", e?.message || e);
  }
}

/** Clear all persisted cache (on logout). */
export async function purgePersistedCache() {
  _getCache.clear();
  try {
    const all = await idbKeys();
    for (const k of all) {
      if (typeof k === "string" && k.startsWith(IDB_PREFIX)) idbDel(k);
    }
  } catch { /* noop */ }
}

api.interceptors.response.use(
  (r) => {
    if (r.config?.method === "get" && r.config?.url) {
      try { setCached(r.config.url, r.data); } catch { /* noop */ }
    }
    return r;
  },
);

api.interceptors.request.use((config) => {
  const tokens = getTokens();
  if (tokens?.access_token) {
    config.headers.Authorization = `Bearer ${tokens.access_token}`;
  }
  return config;
});

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const tokens = getTokens();

    // ─── 402 Paywall Required ────────────────────────────────────────────
    // Backend's PaywallRequired raises 402 with a structured body. Hand it
    // to the global paywall opener so any feature gate (frontend or
    // backend) ends in the same UX. We import lazily to avoid a cycle
    // between api.js and the PaywallContext module.
    if (err.response?.status === 402) {
      const detail = err.response.data?.detail || err.response.data;
      if (detail && detail.code === "paywall_required") {
        try {
          // eslint-disable-next-line global-require
          const { getGlobalPaywallOpener } = require("@/context/PaywallContext");
          getGlobalPaywallOpener()({
            featureKey: detail.feature_key,
            currentTier: detail.current_tier,
            currentTierLabel: detail.current_tier_label,
            requiredTier: detail.required_tier,
            requiredTierLabel: detail.required_tier_label,
            message: detail.message,
          });
        } catch { /* paywall provider not mounted yet — fall through */ }
      }
    }

    // ─── 403 permission.denied (Batch 39/40 polish) ──────────────────────
    // Backend's require_permission(...) raises 403 with a structured detail.
    // Show a friendly toast instead of a raw error string. Components can
    // suppress this by setting config.__suppressPermissionToast = true on the
    // outgoing request when they want to surface inline UI instead.
    if (err.response?.status === 403 && !original.__suppressPermissionToast) {
      const detail = err.response.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "permission.denied") {
        try {
          // eslint-disable-next-line global-require
          const { toast } = require("sonner");
          toast.error(detail.message || "Not allowed", {
            description: "Ask a parent to grant this in Settings → Family → Permissions.",
            duration: 5000,
          });
        } catch { /* sonner not mounted yet */ }
      }
    }

    if (
      err.response?.status === 401 &&
      tokens?.refresh_token &&
      !original.__retried
    ) {
      original.__retried = true;
      try {
        refreshing =
          refreshing ||
          axios.post(
            `${API_BASE}/auth/refresh`,
            {},
            {
              headers: { Authorization: `Bearer ${tokens.refresh_token}` },
              withCredentials: true,
            }
          );
        const { data } = await refreshing;
        refreshing = null;
        setTokens(data);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api.request(original);
      } catch {
        refreshing = null;
        clearTokens();
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
